import { logger } from '@src/app/logger';
import { Locale } from '@superbet-group/web.types';

export function getTranslationsService(
    baseUrl: string,
    locale: Locale,
) {
    let isLoadingTranslations = false;
    let translations: Record<string, string> | null = null;

    async function loadTranslations(): Promise<void> {
        if (isLoadingTranslations) {
            return;
        }

        isLoadingTranslations = true;

        if (translations) {
            return;
        }

        const glyph = resolveLanguageGlyph(locale);

        if (glyph == null) {
            logger.warn(`Sports TranslationsService :: No glyph found for locale: ${locale}`);
            translations = {};
            return;
        }

        const response = await fetch(`${baseUrl}/${glyph}.json`);
        if (!response.ok) {
            logger.warn(`Failed to load translations: ${response.statusText}`);
            // eslint-disable-next-line require-atomic-updates
            translations = {};
            return;
        }

        try {
            const data = await response.json();
            // eslint-disable-next-line require-atomic-updates
            translations = data as unknown as Record<string, string>;
        } catch (error) {
            logger.warn(`Failed to parse translations: ${error}`);
            // eslint-disable-next-line require-atomic-updates
            translations = {};
        } finally {
            // eslint-disable-next-line require-atomic-updates
            isLoadingTranslations = false;
        }
    }

    function getRawTranslation(key: string): string {
        return translations?.[key] ?? key;
    }

    function getTranslations(): Readonly<Record<string, string>> {
        if (!translations) {
            throw new Error('Translations not loaded');
        }
        return translations;
    }

    return {
        loadTranslations,
        getRawTranslation,
        getTranslations,
    };
}

function resolveLanguageGlyph(locale: Locale): string | null {
    switch (locale) {
        case Locale.Romanian:
            return 'ro';
        case Locale.Polish:
            return 'pl';
        case Locale.EnglishBelgian:
            return 'en_BE';
        case Locale.FrenchBelgian:
            return 'fr_BE';
        case Locale.DutchBelgian:
            return 'nl_BE';
        case Locale.Serbian:
            return 'sr_Latn';
        case Locale.PortugueseBrazilian:
            return 'pt_BR';
        case Locale.Bulgarian:
            return 'bg_BG';
        case Locale.English:
            return 'en';
        default:
            return null;
    }
}
